import { useTranslation } from "react-i18next";
import {
  Grid as MuiGrid,
  Typography as MuiTypography,
} from "@mui/material";
import isBefore from "date-fns/isBefore";
import isValid from "date-fns/isValid";
import { format, parseISO } from "date-fns";
import { TrendingUp as TrendingUpIcon } from "@mui/icons-material";
import InfoCard from "./InfoCard";

const SemiCircleProgress = ({ value = 0 }) => {
  const safeValue = Math.min(Math.max(value, 0), 100);
  const radius = 60;
  const circumference = Math.PI * radius;
  const offset = circumference - (safeValue / 100) * circumference;

  return (
    <svg width="150" height="90" role="img" aria-label={`Progress: ${safeValue}%`} >
      <path
        d="M20,80 A60,60 0 0 1 140,80"
        fill="none"
        stroke="#E6E6E6"
        strokeWidth="14"
        strokeLinecap="round"
      />
      <path
        d="M20,80 A60,60 0 0 1 140,80"
        fill="none"
        stroke="#5B6DFF"
        strokeWidth="14"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
      />
      <text
        x="80"
        y="65"
        textAnchor="middle"
        fontSize="16"
        fontWeight="600"
        fill="#333"
        dominantBaseline="middle"
      >
        {Math.round(safeValue)}%
      </text>
    </svg>
  );
};

interface Props {
  enrolled?: number;
  target?: number;
  endDate?: string;
}

const showAccrualDeadlineFeature = false; // todo: replace with actual feature flag

const AccrualProgressCard = ({ enrolled = 0, target = 0, endDate }: Props) => {
  const { t } = useTranslation();
  const progress = target ? (enrolled / target) * 100 : 0;
  let isPast = false;
  let parsed;
  if (endDate) {
    parsed = parseISO(endDate);
    isPast = isValid(parsed) && isBefore(parsed, new Date());
  }

  return (
    <InfoCard
      title={t("studyInsights.accrualProgress")}
      iconComponent={TrendingUpIcon}
      slotProps={{header: { marginBottom: 0 }}}
    >
      <MuiGrid container justifyContent="center" alignItems="flex-start" gap={2}>
        <MuiGrid item>
          <MuiTypography fontWeight={500} fontSize={22} mt={0.8}>
            {`${enrolled} of ${target}`} 
          </MuiTypography>

          {showAccrualDeadlineFeature && endDate && (
            isPast ? (
              <MuiTypography variant="body2" color="text.secondary" mt={0.5}>
                {t("studyInsights.endedIn")} {format(parsed, "MMM ''yy")}
              </MuiTypography>
            ) : (
              <MuiTypography variant="body2" color="text.secondary" mt={0.5}>
                {t("studyInsights.endingIn")} {format(parsed, "MMM ''yy")}
              </MuiTypography>
            )
          )}
        </MuiGrid>

        <MuiGrid item>
          <SemiCircleProgress value={progress} />
        </MuiGrid>

      </MuiGrid>
    </InfoCard>
  );
};

export default AccrualProgressCard;